import { useCallback, useEffect, useState } from "react";
import { Toast } from "primereact/toast";

import useToastActionWrapper from "./useToastActionWrapper";
import {
  getHubSettings,
  updateHubSettings,
} from "../services/onboarding/hubSettings";

interface UseHubSettings {
  hubBaseUrl: string;
  loading: boolean;
  error: boolean;
  reload: () => Promise<void>;
  save: (hubBaseUrl: string) => Promise<boolean>;
}

/**
 * The customer-portal address used in configuration emails (CWA-91). Saving
 * reloads the stored value so the screen never shows an unconfirmed local edit.
 */
const useHubSettings = (toastRef: React.RefObject<Toast>): UseHubSettings => {
  const { toastActionWrapper } = useToastActionWrapper(toastRef);
  const [hubBaseUrl, setHubBaseUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const settings = await getHubSettings();
      setHubBaseUrl(settings?.hub_base_url ?? "");
    } catch (e) {
      console.error("Failed to load the portal address", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(
    async (value: string): Promise<boolean> => {
      let ok = false;
      await toastActionWrapper({
        action: async () => {
          const saved = await updateHubSettings({ hub_base_url: value });
          setHubBaseUrl(saved?.hub_base_url ?? value);
          ok = true;
          return true;
        },
        successMessage: "Portal address saved.",
        errorMessage: "The portal address could not be saved.",
      });
      return ok;
    },
    [toastActionWrapper]
  );

  return { hubBaseUrl, loading, error, reload, save };
};

export default useHubSettings;
